import { useState } from 'react';
import Joi from 'joi';
import { validationOptions, validationMessage } from '../i18n/validation';

const useForm = (initialData = {}, schema = {}) => {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});

  // `lyricsOptional` for a part whose chart is its content, which leaves it no words to require
  const validatePart = (part, { lyricsOptional = false } = {}) => {
    const lyrics = Joi.string().label('Lyrics and Chords');
    const partSchema = {
      currentLyrics: lyricsOptional ? lyrics.allow('') : lyrics.required(),
      partNumber: Joi.number().required().min(1).max(99).label('Number'),
    };

    const { error } = Joi.object(partSchema).validate(part, { ...validationOptions, abortEarly: false });

    return getErrors(error);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const propertySchema = { [name]: schema[name] };

    const { error } = Joi.object(propertySchema).validate(obj, validationOptions);

    return error ? validationMessage(error.details[0]) : null;
  };

  const validate = () => {
    const { error } = Joi.object(schema).validate(data, { ...validationOptions, abortEarly: false });

    return getErrors(error);
  };

  const getErrors = (error) => {
    if (!error) return null;
    // loop through errors
    const errors = {};

    for (let item of error.details) errors[item.path[0]] = validationMessage(item);

    return errors;
  };

  const handleChange = ({ currentTarget: input }) => {
    // validate
    const newErrors = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];

    // confirm password
    if (data.password === input.value) {
      delete newErrors.confirmPassword;
    }

    setErrors(newErrors);

    // set the value for the input every time it changes
    setInputToState(input);
  };

  const setInputToState = (input) => {
    let inputElement = input;

    if ('currentTarget' in input) {
      inputElement = input.currentTarget;
    }

    const newData = { ...data };
    newData[inputElement.name] = inputElement.value;

    setData(newData);
  };

  const handleSubmit = (e, doSubmit) => {
    e.preventDefault();

    const validationErrors = validate();

    setErrors(validationErrors || {});

    if (validationErrors) return;

    doSubmit();
  };

  const isFormValid = () => {
    return !validate();
  };

  const resetForm = (newData = initialData) => {
    setData(newData);
    setErrors({});
  };

  return {
    data,
    errors,
    setData,
    setErrors,
    validatePart,
    validateProperty,
    validate,
    handleChange,
    setInputToState,
    handleSubmit,
    isFormValid,
    resetForm,
  };
};

export default useForm;
